// ─── realtime.js — Supabase Realtime subscription manager ───────────────────
// Handles all live data: agent events, pipeline counts, conversation feed,
// and relationship drift. Drives panels.js UI updates + agentCtx animations.

import { createClient } from '@supabase/supabase-js'
import {
  updatePipelineCounts,
  addFeedEntry,
  setConnectionStatus,
  showStatCard,
  getSelectedAgent,
  updateAgentDot,
} from './panels.js'

// ─── Config ──────────────────────────────────────────────────────────────────
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://jxworpavuinxskkalvwu.supabase.co'
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// ─── Module State ────────────────────────────────────────────────────────────

/** @type {import('@supabase/supabase-js').SupabaseClient | null} */
let supabase = null

/**
 * agentCtx provided by scene — exposes processEvent, agents, showSpeechBubble.
 * @type {{ processEvent: Function, agents: Object, showSpeechBubble: Function } | null}
 */
let agentCtx = null

/**
 * Cached agent stats keyed by agent name.
 * Each value is the full row from dashboard_stats.
 * @type {Record<string, Object>}
 */
const statsCache = {}

// ─── Content-queue status → agent visual state mapping ───────────────────────
const QUEUE_STATUS_AGENT_MAP = {
  generating: { agent: 'pixel', state: 'working' },
  ready:      { agent: 'pixel', state: 'celebrating' },
  posting:    { agent: 'echo',  state: 'working' },
  posted:     { agent: 'echo',  state: 'celebrating' },
  failed:     { agent: 'pixel', state: 'frustrated' },
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Initialize Supabase Realtime: stores agentCtx reference, creates the
 * Supabase client, then loads initial state and sets up live subscriptions.
 *
 * @param {{ processEvent: Function, agents: Object, showSpeechBubble: Function }} ctx
 */
export function initRealtime(ctx) {
  agentCtx = ctx

  if (!SUPABASE_ANON_KEY) {
    console.warn('[Realtime] VITE_SUPABASE_ANON_KEY not set — running in demo mode')
    setConnectionStatus(false)
    return
  }

  supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

  loadInitialState()
  setupSubscriptions()
}

/**
 * Return cached stats for a given agent, or null if not yet loaded.
 *
 * @param {string} agent - Agent name (e.g. 'atlas', 'muse')
 * @returns {Object | null}
 */
export function getStats(agent) {
  return statsCache[agent] ?? null
}

// ─── Initial State ───────────────────────────────────────────────────────────

/**
 * Fetches current snapshot from Supabase:
 * - Agent stats   → statsCache
 * - Pipeline counts → updatePipelineCounts()
 * - Recent conversations → addFeedEntry()
 * Sets connection status true on success, false on error.
 */
async function loadInitialState() {
  if (!supabase) return

  try {
    // ── 1. Agent stats ────────────────────────────────────────────────────────
    const { data: stats, error: statsErr } = await supabase
      .from('dashboard_stats')
      .select('*')

    if (statsErr) {
      console.warn('[Realtime] dashboard_stats fetch failed:', statsErr.message)
    } else if (stats) {
      for (const row of stats) {
        statsCache[row.agent] = row
      }
    }

    // ── 2. Pipeline counts (4 parallel queries) ───────────────────────────────
    const [pendingRes, generatingRes, readyRes, postedRes] = await Promise.all([
      supabase.from('content_queue').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('content_queue').select('id', { count: 'exact', head: true }).eq('status', 'generating'),
      supabase.from('content_queue').select('id', { count: 'exact', head: true }).eq('status', 'ready'),
      supabase.from('content_queue').select('id', { count: 'exact', head: true }).eq('status', 'posted'),
    ])

    updatePipelineCounts({
      pending:    pendingRes.count    ?? 0,
      generating: generatingRes.count ?? 0,
      ready:      readyRes.count      ?? 0,
      posted:     postedRes.count     ?? 0,
    })

    // ── 3. Recent conversations + events (parallel fetch) ──────────────────────
    const [convosRes, eventsRes] = await Promise.all([
      supabase
        .from('dashboard_conversations')
        .select('*')
        .order('message_at', { ascending: false })
        .limit(20),
      supabase
        .from('dashboard_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20),
    ])

    if (convosRes.error) {
      console.warn('[Realtime] dashboard_conversations fetch failed:', convosRes.error.message)
    }
    if (eventsRes.error) {
      console.warn('[Realtime] dashboard_events fetch failed:', eventsRes.error.message)
    }

    // Merge conversations and events into a single feed, sorted by timestamp
    const feedItems = []

    if (convosRes.data) {
      for (const c of convosRes.data) {
        if (c.message) {
          feedItems.push({
            agent: c.message_agent,
            message: c.message,
            timestamp: c.message_at,
            eventType: c.conversation_type,
          })
        }
      }
    }

    if (eventsRes.data) {
      for (const e of eventsRes.data) {
        // Extract a human-readable summary from payload if available
        const summary = e.payload?.summary || e.payload?.message || e.payload?.detail || null
        const eventLabel = e.event_type
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
        feedItems.push({
          agent: e.agent,
          message: summary || eventLabel,
          timestamp: e.created_at,
          eventType: e.event_type,
        })
      }
    }

    // Sort oldest-first so prepend order is correct (newest ends up on top)
    feedItems.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

    // Deduplicate: if a conversation message and event have the same agent+timestamp, skip the event
    const seen = new Set()
    for (const item of feedItems) {
      const key = `${item.agent}:${item.timestamp}`
      if (seen.has(key)) continue
      seen.add(key)
      addFeedEntry(item.agent, item.message, item.timestamp, item.eventType)
    }

    // ── 4. Mark connected ─────────────────────────────────────────────────────
    setConnectionStatus(true)

  } catch (err) {
    console.error('[Realtime] loadInitialState error:', err)
    setConnectionStatus(false)
  }
}

// ─── Subscriptions ───────────────────────────────────────────────────────────

/**
 * Sets up the four Supabase Realtime channels.
 * Each channel is independent; failures in one do not affect others.
 */
function setupSubscriptions() {
  if (!supabase) return

  subscribeAgentEvents()
  subscribeContentQueue()
  subscribeConversationMessages()
  subscribeRelationshipDrift()
}

// ── Channel 1: agent_events ───────────────────────────────────────────────────

/**
 * Listens for INSERT on agent_events.
 * Drives agent visual state and adds a feed entry for each event.
 * Controls the connection status indicator via subscribe callback.
 */
function subscribeAgentEvents() {
  supabase
    .channel('agent-events')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'agent_events' },
      (payload) => {
        const { agent, event_type, payload: eventPayload, created_at } = payload.new

        if (!agent || !event_type) return

        // Drive 3D scene animation
        if (agentCtx?.processEvent) {
          agentCtx.processEvent(agent, event_type)
        }

        // Derive visual dot state from event type
        const EVENT_STATE_MAP = {
          workflow_run:              'working',
          workflow_completed:        'idle',
          content_brief_created:     'working',
          content_ready:             'celebrating',
          content_generation_failed: 'frustrated',
          content_posted:            'celebrating',
          posting_failed:            'frustrated',
          outside_posting_window:    'idle',
          daily_report:              'working',
          heartbeat_complete:        'idle',
          morning_roundtable:        'talking',
          fatal_error:               'frustrated',
          stats_computed:            'idle',
          stale_claim_reset:         'working',
        }
        const state = EVENT_STATE_MAP[event_type] || 'idle'

        // Update the agent dot in the bottom bar
        updateAgentDot(agent, state)

        // Build a meaningful message from payload if available
        const summary = eventPayload?.summary || eventPayload?.message || eventPayload?.detail || null
        const eventLabel = event_type
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')

        addFeedEntry(agent, summary || eventLabel, created_at, event_type)

        console.log(`[Realtime] agent_events: ${agent} → ${event_type} (${state})`)
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        setConnectionStatus(true)
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        setConnectionStatus(false)
      }
    })
}

// ── Channel 2: content_queue ──────────────────────────────────────────────────

/**
 * Listens for UPDATE on content_queue.
 * Maps queue status to agent visual states, adds feed entries with persona name,
 * then refreshes pipeline counts by re-fetching initial state.
 */
function subscribeContentQueue() {
  supabase
    .channel('content-queue')
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'content_queue' },
      (payload) => {
        const { status, persona_name, updated_at } = payload.new

        if (!status) return

        // Map status → agent visual state
        const mapping = QUEUE_STATUS_AGENT_MAP[status]
        if (mapping) {
          if (agentCtx?.processEvent) {
            agentCtx.processEvent(mapping.agent, `content_queue_${status}`)
          }
          updateAgentDot(mapping.agent, mapping.state)
        }

        // Feed entry uses persona name as the "agent" label so it's identifiable
        const entryAgent = mapping?.agent ?? 'system'
        const personaLabel = persona_name ? `${persona_name}: ${status}` : status
        addFeedEntry(entryAgent, personaLabel, updated_at ?? new Date().toISOString(), `content_${status}`)

        console.log(`[Realtime] content_queue: ${persona_name ?? '?'} → ${status}`)

        // Refresh all pipeline counts
        loadInitialState()
      }
    )
    .subscribe()
}

// ── Channel 3: agent_conversation_messages ────────────────────────────────────

/**
 * Listens for INSERT on agent_conversation_messages.
 * Triggers talking state on the scene agent, shows a speech bubble,
 * and logs the message to the activity feed.
 */
function subscribeConversationMessages() {
  supabase
    .channel('conversation-messages')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'agent_conversation_messages' },
      (payload) => {
        const { agent, message, created_at } = payload.new

        if (!agent || !message) return

        // Trigger talking state + roundtable movement on the scene
        if (agentCtx?.processEvent) {
          agentCtx.processEvent(agent, 'morning_roundtable')
        }

        // Update dot to talking state
        updateAgentDot(agent, 'talking')

        // Show speech bubble over the 3D agent
        if (agentCtx?.showSpeechBubble) {
          agentCtx.showSpeechBubble(agent, message)
        }

        // Add to activity feed
        addFeedEntry(agent, message, created_at, 'morning_roundtable')

        console.log(`[Realtime] conversation_messages: ${agent}: ${message.substring(0, 60)}`)
      }
    )
    .subscribe()
}

// ── Channel 4: agent_relationship_drift ──────────────────────────────────────

/**
 * Listens for INSERT on agent_relationship_drift.
 * Console log only for now — visual flash between agents is a future enhancement.
 */
function subscribeRelationshipDrift() {
  supabase
    .channel('relationship-drift')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'agent_relationship_drift' },
      (payload) => {
        const { triggered_by, drift, agent_a, agent_b } = payload.new
        console.log(
          `[Drift] triggered_by=${triggered_by} agents=${agent_a}↔${agent_b} drift=${drift}`
        )
      }
    )
    .subscribe()
}
